import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useStore } from "zustand";

import GoBack from "../../../assets/GoBackApp.svg";
import AssetIcon from "../components/asset-icon";

import { marketStore } from "entities/market";
import { sircapApi } from "shared/api/sircap";
import { PATH_LIST } from "shared/lib/react-router";
import { AssetType } from "static/types";
import useExchangeTimeFrame from "hooks/useExchangeTimeFrame";
import OptionChainRow, {
  buildOptionStrikeMap,
  filterStrikes,
} from "../components/option-chain-row";

import "./style.scss";
import HorizontalScroll from "../components/horizontal-scroll";

interface IOptionContract {
  id: number;
  gtn_key: string;
  symbol: string;
  strike: number;
  expiration: string;
  option_type: number;
  lot_size: number;
  decimal_places: number;
  bid: number | null;
  ask: number | null;
  price: number | null;
  open_interest: number | null;
  display_ticker: string | null;
}

interface IOptionChain {
  asset_id: number | null;
  expirations: string[];
  calls: IOptionContract[];
  puts: IOptionContract[];
}

export const OptionDetailsPage: React.FC = () => {
  const { t } = useTranslation("app");
  const navigate = useNavigate();
  const { asset_id } = useParams<{ asset_id: string }>();
  const { loadAssetInfo, assetInfo, isInitialLoading } = useStore(marketStore);
  const [chain, setChain] = useState<IOptionChain | null>(null);
  const [allExpirations, setAllExpirations] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedExp, setSelectedExp] = useState<string>("");

  useEffect(() => {
    if (asset_id) loadAssetInfo(asset_id);
  }, [asset_id]);

  const fetchChain = useCallback(
    async (expiration?: string) => {
      if (!asset_id) return;
      setLoading(true);
      try {
        const resp = await sircapApi.market.optionChain(+asset_id)({
          params: expiration ? { expiration } : {},
        });
        if (!resp.error && resp.data?.data) {
          const data = resp.data.data as IOptionChain;
          setChain(data);
          if (data.expirations?.length) {
            setAllExpirations((prev) =>
              prev.length ? prev : data.expirations,
            );
            setSelectedExp((prev) => prev || data.expirations[0]);
          }
        }
      } finally {
        setLoading(false);
      }
    },
    [asset_id],
  );

  useEffect(() => {
    fetchChain();
  }, [fetchChain]);

  const handleExpChange = (exp: string) => {
    setSelectedExp(exp);
    fetchChain(exp);
  };

  const goToDeal = (
    operation: "buy" | "sell",
    contract: IOptionContract,
    price: number,
  ) => {
    const dealAssetId = chain?.asset_id ?? asset_id;
    if (!dealAssetId || !contract.id) return;
    const params = new URLSearchParams({
      type: "10",
      price: String(price),
      expiration: contract.expiration,
      strike: String(contract.strike),
      option_type: String(contract.option_type),
    });
    params.set("contract_id", String(contract.id));
    navigate(`${PATH_LIST[operation](dealAssetId)}?${params.toString()}`);
  };

  const exchange = useExchangeTimeFrame(assetInfo?.asset?.exchange);

  const symbol = assetInfo?.asset?.symbol || "";
  const companyName =
    (assetInfo as any)?.info?.companyName || assetInfo?.asset?.name || "";
  const expirations = allExpirations.length
    ? allExpirations
    : chain?.expirations || [];

  const strikeMap = useMemo(() => {
    if (!chain)
      return new Map<
        number,
        { call?: IOptionContract; put?: IOptionContract }
      >();
    return buildOptionStrikeMap(chain.calls, chain.puts, selectedExp);
  }, [chain, selectedExp]);

  const strikes = useMemo(
    () =>
      filterStrikes(
        Array.from(strikeMap.keys()).sort((a, b) => a - b),
      ),
    [strikeMap],
  );

  const isFirstLoad = loading && !chain;

  if (isInitialLoading || !assetInfo || isFirstLoad) {
    return (
      <div className="option-details">
        <div className="option-details-header">
          <img
            className="go-back-portfolio"
            onClick={() => navigate(-1)}
            src={GoBack}
            alt="Go Back"
          />
          <div className="option-details-header-info">
            <p>{symbol}</p>
          </div>
        </div>
        <div className="option-details-main">
          <p className="assets-loading assets-loading-page">{t("loading")}</p>
        </div>
      </div>
    );
  }

  return (
    <div id="option-details" className="option-details">
      <div className="option-details-header">
        <img
          className="go-back-portfolio"
          onClick={() => navigate(-1)}
          src={GoBack}
          alt="Go Back"
        />
        <div className="option-details-header-info">
          <AssetIcon
            asset={
              assetInfo?.asset || {
                type: assetInfo?.asset?.type || AssetType.Stock,
                symbol: symbol,
              }
            }
            className="asset-logo"
          />
          <p>
            {symbol}
            <span>{companyName ? `(${companyName})` : t("options")}</span>
          </p>
        </div>
      </div>

      {expirations.length > 0 && (
        <HorizontalScroll className="option-details-exp-tabs">
          {expirations.map((exp) => (
            <button
              key={exp}
              className={`option-details-exp-tab ${
                selectedExp === exp ? "active" : ""
              }`}
              onClick={() => handleExpChange(exp)}
            >
              {exp}
            </button>
          ))}
        </HorizontalScroll>
      )}

      <div className="option-details-main">
        {loading ? (
          <p className="option-details-loading">{t("loading")}</p>
        ) : strikes.length === 0 ? (
          <p className="option-details-empty">{t("notFound")}</p>
        ) : (
          <table className="option-details-table">
            <thead>
              <tr>
                <th colSpan={2} className="call-h">{t("optionsCall")}</th>
                <th className="strike-h">{t("optionsStrike")}</th>
                <th colSpan={2} className="put-h">{t("optionsPut")}</th>
              </tr>
              <tr>
                <th>{t("optionsBid")}</th>
                <th>{t("optionsAsk")}</th>
                <th className="strike-c">—</th>
                <th>{t("optionsBid")}</th>
                <th>{t("optionsAsk")}</th>
              </tr>
            </thead>
            <tbody>
              {strikes.map((strike) => {
                const { call, put } = strikeMap.get(strike) || {};
                return (
                  <OptionChainRow
                    key={strike}
                    call={call}
                    put={put}
                    strike={strike}
                    parentSymbol={symbol}
                    exchange={exchange?.exchange_code}
                    exchangeStatus={exchange?.status}
                    activeClass="has-p clickable"
                    strikeClass="strike-c"
                    onDeal={goToDeal}
                  />
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};
